import type { SessionContext } from "../../lib/sessionContext"

// The context-usage bar, and the rules about what it may claim.
//
// Shape follows the TUI's composer footer — "124,318 (62%)" — rather than the
// desktop's ring, because this sits in a row of text and a ring next to
// monospace digits reads as a spinner.

const EM_DASH = "—"

/** Where the bar changes colour. Only the last of these is a real event: the
 *  agent compacts near the top of the window, so 95% is "history is about to be
 *  summarised away", not merely "getting full". */
const WARNING_AT = 80
const DANGER_AT = 95

export function meterTone(usage: number | null): "muted" | "warning" | "danger" {
  if (usage === null) return "muted"
  if (usage >= DANGER_AT) return "danger"
  if (usage >= WARNING_AT) return "warning"
  return "muted"
}

const FILL = {
  muted: "bg-muted",
  warning: "bg-warning",
  danger: "bg-danger",
} as const

const TEXT = {
  muted: "text-muted",
  warning: "text-warning",
  danger: "text-danger",
} as const

export function ContextMeter({
  context,
  onClick,
  expanded,
}: {
  context: SessionContext | undefined
  onClick: () => void
  expanded: boolean
}) {
  const usage = context?.usage ?? null
  const tone = meterTone(usage)

  // Three states, and they are genuinely different claims:
  //   no context   — nothing has been measured yet (no turn has completed)
  //   usage null   — measured, but the model's window is not known
  //   usage number — measured against a known window
  //
  // The middle one is why this cannot fall back to 0%. A window nobody has
  // filled in is not an empty window; see lib/sessionContext.ts.
  const title = !context
    ? "No turn has completed yet, so nothing has been measured."
    : usage === null
      ? `${context.total.toLocaleString()} tokens used. The model's context window is not configured, so this cannot be shown as a percentage.`
      : `${context.total.toLocaleString()} of ${context.limit?.toLocaleString()} tokens (${usage}%)`

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-expanded={expanded}
      aria-label="Context usage — click for a breakdown"
      className="inline-flex items-center gap-2 rounded px-1.5 py-1 transition-colors hover:bg-surface-muted"
    >
      <span className="h-1 w-12 overflow-hidden rounded-full bg-surface-muted" aria-hidden>
        <span
          className={`block h-full rounded-full transition-[width] ${FILL[tone]}`}
          // Clamped: a provider that reports more input than the configured
          // window would otherwise render a bar wider than its own track.
          style={{ width: `${Math.min(100, Math.max(0, usage ?? 0))}%` }}
        />
      </span>
      <span className={`font-mono text-xs tabular-nums ${TEXT[tone]}`}>
        {context ? context.total.toLocaleString() : EM_DASH}
        {usage !== null && <span className="ml-1 text-faint">({usage}%)</span>}
      </span>
    </button>
  )
}
